import axiosInstance, { endpoints } from 'src/lib/axios';
import { medOrgHuvud } from 'src/lib/org-huvud';

// ----------------------------------------------------------------------

/**
 * Egenkontrollen: de återkommande kontroller en fastighetsägare är skyldig att låta göra.
 *
 * Status och förfallodag räknas fram av backend vid varje anrop — de lagras inte, eftersom en
 * lagrad förfallodag är sann till natten den passerar och sedan en lögn ingen rättar. Lagrummet
 * följer med varje rad, så gränssnittet kan visa varför kravet finns utan att upprepa
 * föreskrifterna i TypeScript.
 */
export type Egenkontrollstatus =
  | 'ALDRIG_KONTROLLERAD'
  | 'AKTUELL'
  | 'SNART_FÖRFALLEN'
  | 'FÖRFALLEN'
  | 'UNDERKÄND';

export type Kontrollresultat = 'GODKÄND' | 'GODKÄND_MED_ANMÄRKNING' | 'UNDERKÄND';

export type IEgenkontrolluppgift = {
  kontrollobjektId: string;
  byggnadId?: string;
  postId?: string;
  typ: string;
  typEtikett: string;
  benämning: string;
  status: Egenkontrollstatus;
  statusEtikett: string;
  intervallMånader: number;
  senasteKontrollDatum?: string;
  senasteResultat?: Kontrollresultat;
  förfallodag?: string;
  dagarKvar?: number;
  öppnaAnmärkningar: number;
  anmärkningarMedPasseradFrist: number;
  kräverAnslag: boolean;
  väntarPåRapportering: boolean;
  mätvärdeÖverträderGräns: boolean;
  kräverÅtgärd: boolean;
  lagrum: string;
};

/**
 * Hela registret som det ser ut idag, det som kräver åtgärd först.
 *
 * `medOrgHuvud` är med för att hubben läser andra föreningar än den valda — utan huvudet
 * utelämnas organisationen helt i hubbläget och anropet avvisas.
 */
export async function hämtaEgenkontrollÖversikt(orgId: string): Promise<IEgenkontrolluppgift[]> {
  const svar = await axiosInstance.get<IEgenkontrolluppgift[]>(
    endpoints.fastighet.egenkontroll.översikt,
    medOrgHuvud(orgId)
  );
  return Array.isArray(svar.data) ? svar.data : [];
}

/** Det som förfaller inom så många dagar och ännu inte är förfallet. */
export async function hämtaKommandeEgenkontroller(
  orgId: string,
  inomDagar = 90
): Promise<IEgenkontrolluppgift[]> {
  const svar = await axiosInstance.get<IEgenkontrolluppgift[]>(endpoints.fastighet.egenkontroll.kommande, {
    params: { inomDagar },
    ...medOrgHuvud(orgId),
  });
  return Array.isArray(svar.data) ? svar.data : [];
}

// ---------------------------------------------------------------------- katalog och register

/** Kontrolltypens lagkrav, levererade som data så att formuläret byggs av dem. */
export type IKontrolltyp = {
  typ: string;
  etikett: string;
  standardintervallMånader: number;
  förvarningsdagar: number;
  kontrollorgankrav: string;
  kräverBehörighetsnummer: boolean;
  anslagskrav: boolean;
  rapporteringsmottagare: string;
  mätenhet?: string;
  gränsvärde?: number;
  gränsriktning?: 'HÖGST' | 'LÄGST';
  /** Hur mätningen ska göras, för de typer där metoden är en del av kravet. */
  mätanvisning?: string;
  /** Punkterna kontrollen normalt går igenom. Tom för de typer en besiktningsman protokollför. */
  punktmall: string[];
  /** Sant när punkterna bär ett tal och inte bara ett utfall. */
  punkternaMäter: boolean;
  lagrum: string;
};

export type Punktresultat = 'OK' | 'ANMÄRKNING' | 'EJ_TILLÄMPLIG';

/**
 * En rad i ett protokoll.
 *
 * Brandskyddsronden, skyddsrumskontrollen och temperaturkontrollen protokollförs punkt för punkt.
 * En punkt med anmärkning blir en anmärkning med egen frist — det är skillnaden mot en rond som
 * dokumenteras som en pdf i en mapp.
 */
export type IKontrollpunkt = {
  id?: string;
  benämning: string;
  resultat: Punktresultat;
  resultatEtikett?: string;
  mätvärde?: number;
  anteckning?: string;
  ordning?: number;
};

export type IKontrollobjekt = {
  id?: string;
  byggnadId?: string;
  postId?: string;
  typ: string;
  typEtikett?: string;
  benämning: string;
  placering?: string;
  myndighetsbeteckning?: string;
  intervallMånaderÖverstyrt?: number;
  intervallMånader?: number;
  ventilationssystem?: string;
  ansvarigAnvändareId?: string;
  aktiv: boolean;
  anteckning?: string;
  ursprung?: 'GENERERAD' | 'MANUELL' | 'IMPORT';
  ändradAvAnvändare?: boolean;
  kräverAnslag?: boolean;
  lagrum?: string;
};

export type IAnmärkning = {
  id?: string;
  kontrollId?: string;
  kontrollobjektId?: string;
  beskrivning: string;
  allvar: 'KRITISK' | 'ALLVARLIG' | 'MINDRE';
  allvarEtikett?: string;
  åtgärdsfrist?: string;
  status?: 'ÖPPEN' | 'PÅGÅENDE' | 'ÅTGÄRDAD' | 'AVSKRIVEN';
  statusEtikett?: string;
  ärendeId?: string;
  åtgärdadDatum?: string;
  åtgärdskommentar?: string;
  fristPasserad?: boolean;
};

export type IUtfördKontroll = {
  id: string;
  kontrollobjektId: string;
  typ: string;
  typEtikett: string;
  utfördDatum: string;
  resultat: Kontrollresultat;
  resultatEtikett: string;
  kontrollantNamn?: string;
  kontrollantFöretag?: string;
  kontrollantBehörighetsnummer?: string;
  mätvärde?: number;
  mätenhet?: string;
  mätvärdeÖverträderGräns: boolean;
  giltigTill?: string;
  protokollUrl?: string;
  protokollFilnamn?: string;
  rapporteradDatum?: string;
  rapporteringsreferens?: string;
  väntarPåRapportering: boolean;
  anteckning?: string;
  anmärkningar: IAnmärkning[];
  punkter: IKontrollpunkt[];
};

export type IRegistreraKontroll = {
  utfördDatum: string;
  resultat: Kontrollresultat;
  kontrollantNamn?: string;
  kontrollantFöretag?: string;
  kontrollantBehörighetsnummer?: string;
  mätvärde?: number;
  rapporteradDatum?: string;
  rapporteringsreferens?: string;
  anteckning?: string;
  anmärkningar?: IAnmärkning[];
  punkter?: IKontrollpunkt[];
};

export type IRegisteruppdatering = {
  skapade: number;
  uppdaterade: number;
  avaktiverade: number;
  borttagna: number;
  orörda: number;
  totalt?: number;
};

/**
 * Statusfärgerna.
 *
 * Fyra av fem lägen är röda eller gula, och det är avsiktligt: ett register där det mesta ser
 * grönt ut men inget är kontrollerat hade varit sämre än inget register. `UNDERKÄND` är den
 * allvarligaste — för en hiss betyder den användningsförbud.
 */
export const STATUSFÄRG: Record<Egenkontrollstatus, 'success' | 'warning' | 'error' | 'default'> = {
  AKTUELL: 'success',
  SNART_FÖRFALLEN: 'warning',
  FÖRFALLEN: 'error',
  UNDERKÄND: 'error',
  ALDRIG_KONTROLLERAD: 'warning',
};

export const RESULTATFÄRG: Record<Kontrollresultat, 'success' | 'warning' | 'error'> = {
  GODKÄND: 'success',
  GODKÄND_MED_ANMÄRKNING: 'warning',
  UNDERKÄND: 'error',
};

export const PUNKTFÄRG: Record<Punktresultat, 'success' | 'error' | 'default'> = {
  OK: 'success',
  ANMÄRKNING: 'error',
  EJ_TILLÄMPLIG: 'default',
};

export const ALLVARFÄRG: Record<IAnmärkning['allvar'], 'error' | 'warning' | 'default'> = {
  KRITISK: 'error',
  ALLVARLIG: 'warning',
  MINDRE: 'default',
};

/** Intervallet i klartext. Månader är exakt men oläsligt: "var 36:e månad" säger ingen. */
export function intervallText(månader?: number): string {
  if (!månader) return '—';
  if (månader % 12 === 0) {
    const år = månader / 12;
    return år === 1 ? 'Varje år' : `Var ${år}:e år`;
  }
  if (månader === 1) return 'Varje månad';
  return `Var ${månader}:e månad`;
}

// ---------------------------------------------------------------------- anrop

export async function hämtaKontrolltyper(orgId: string): Promise<IKontrolltyp[]> {
  const svar = await axiosInstance.get<IKontrolltyp[]>(
    endpoints.fastighet.egenkontroll.typer,
    medOrgHuvud(orgId)
  );
  return Array.isArray(svar.data) ? svar.data : [];
}

export async function hämtaKontrollobjekt(orgId: string): Promise<IKontrollobjekt[]> {
  const svar = await axiosInstance.get<IKontrollobjekt[]>(
    endpoints.fastighet.egenkontroll.objekt,
    medOrgHuvud(orgId)
  );
  return Array.isArray(svar.data) ? svar.data : [];
}

export async function sparaKontrollobjekt(
  orgId: string,
  objekt: IKontrollobjekt
): Promise<IKontrollobjekt> {
  const svar = objekt.id
    ? await axiosInstance.put<IKontrollobjekt>(
        endpoints.fastighet.egenkontroll.objektMedId(objekt.id),
        objekt,
        medOrgHuvud(orgId)
      )
    : await axiosInstance.post<IKontrollobjekt>(
        endpoints.fastighet.egenkontroll.objekt,
        objekt,
        medOrgHuvud(orgId)
      );
  return svar.data;
}

/** Stänger objektet. Backend raderar bara det som aldrig kontrollerats — resten avaktiveras. */
export async function stängKontrollobjekt(orgId: string, objektId: string): Promise<void> {
  await axiosInstance.delete(
    endpoints.fastighet.egenkontroll.objektMedId(objektId),
    medOrgHuvud(orgId)
  );
}

export async function regenereraRegister(orgId: string): Promise<IRegisteruppdatering> {
  const svar = await axiosInstance.post<IRegisteruppdatering>(
    endpoints.fastighet.egenkontroll.generera,
    {},
    medOrgHuvud(orgId)
  );
  return svar.data;
}

export async function hämtaHistorik(orgId: string, objektId: string): Promise<IUtfördKontroll[]> {
  const svar = await axiosInstance.get<IUtfördKontroll[]>(
    endpoints.fastighet.egenkontroll.historik(objektId),
    medOrgHuvud(orgId)
  );
  return Array.isArray(svar.data) ? svar.data : [];
}

export async function registreraKontroll(
  orgId: string,
  objektId: string,
  begäran: IRegistreraKontroll
): Promise<IUtfördKontroll> {
  const svar = await axiosInstance.post<IUtfördKontroll>(
    endpoints.fastighet.egenkontroll.registreraKontroll(objektId),
    begäran,
    medOrgHuvud(orgId)
  );
  return svar.data;
}

/** Protokollet laddas upp i ett eget steg — filen kommer ofta senare än uppgiften. */
export async function laddaUppProtokoll(
  orgId: string,
  kontrollId: string,
  fil: File
): Promise<IUtfördKontroll> {
  const data = new FormData();
  data.append('fil', fil);
  const svar = await axiosInstance.post<IUtfördKontroll>(
    endpoints.fastighet.egenkontroll.protokoll(kontrollId),
    data,
    { headers: { 'Content-Type': 'multipart/form-data', ...medOrgHuvud(orgId).headers } }
  );
  return svar.data;
}

export async function kvitteraRapportering(
  orgId: string,
  kontrollId: string,
  rapporteradDatum: string,
  referens?: string
): Promise<IUtfördKontroll> {
  const svar = await axiosInstance.put<IUtfördKontroll>(
    endpoints.fastighet.egenkontroll.rapporterad(kontrollId),
    { rapporteradDatum, referens },
    medOrgHuvud(orgId)
  );
  return svar.data;
}

export async function hämtaAnmärkningarFörObjekt(
  orgId: string,
  objektId: string
): Promise<IAnmärkning[]> {
  const svar = await axiosInstance.get<IAnmärkning[]>(
    endpoints.fastighet.egenkontroll.anmärkningarFörObjekt(objektId),
    medOrgHuvud(orgId)
  );
  return Array.isArray(svar.data) ? svar.data : [];
}

export async function åtgärdaAnmärkning(
  orgId: string,
  anmärkningId: string,
  åtgärdadDatum: string,
  kommentar?: string
): Promise<IAnmärkning> {
  const svar = await axiosInstance.put<IAnmärkning>(
    endpoints.fastighet.egenkontroll.åtgärdaAnmärkning(anmärkningId),
    { åtgärdadDatum, kommentar },
    medOrgHuvud(orgId)
  );
  return svar.data;
}

/**
 * Hämtar anslaget som pdf och öppnar det i en ny flik.
 *
 * Öppet för boende, till skillnad från resten av registret: OVK-intyget och energideklarationens
 * sammanfattning ska sitta på en synlig plats i byggnaden, och den som bor där ska kunna skriva ut
 * det själv utan att be styrelsen.
 */
export async function öppnaAnslag(orgId: string, byggnadId?: string): Promise<void> {
  const svar = await axiosInstance.get<Blob>(endpoints.fastighet.egenkontroll.anslag, {
    params: byggnadId ? { byggnadId } : undefined,
    responseType: 'blob',
    ...medOrgHuvud(orgId),
  });

  const url = URL.createObjectURL(new Blob([svar.data], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener');
  // Webbläsaren behöver hinna läsa objektet innan det släpps; minuten är gott och väl nog och
  // undviker att varje utskrift lämnar kvar en blob i minnet.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
